import { CameraSpeedGear } from 'components/PTZ';
import { useCameraPTZ } from 'components/PTZ/hooks';
import { JoystickContext, VideoHotkeyAction } from 'contexts';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface PTZContextState {
  panSpeed: number;
  zoomSpeed: number;
  tiltSpeed: number;
  setPanTiltSpeed: (pan: number, tilt: number) => void;
  setZoomSpeed: (speed: number) => void;
}

export interface CameraSelectionContextState {
  selectedCameraId: string | null;
  setSelectedCameraId: (cameraId: string) => void;
  goToNextCamera: () => void;
  goToPreviousCamera: () => void;
  goToLastCamera: () => void;
  goToCamera: (index: number) => void;
}
export const CameraSelectionContext = createContext<CameraSelectionContextState>({
  selectedCameraId: null,
  setSelectedCameraId: () => null,
  goToNextCamera: () => null,
  goToPreviousCamera: () => null,
  goToLastCamera: () => null,
  goToCamera: () => null,
});

export const useCameraSelectionContext = (cameraIds: string[]): CameraSelectionContextState => {
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(cameraIds[0]);
  const [selectedCameraIndex, setSelectedCameraIndex] = useState<number>(0);
  const lastCameraIdRef = useRef<string | null>(null);
  const { currentAction } = useContext(JoystickContext);
  const setSelectedCameraIdWithLastCamera = useCallback(
    (cameraId: string) => {
      setSelectedCameraId((prev) => {
        lastCameraIdRef.current = prev;
        return cameraId;
      });
    },
    [setSelectedCameraId]
  );
  useEffect(() => {
    if (!selectedCameraId && cameraIds.length > 0) {
      setSelectedCameraIdWithLastCamera(cameraIds[selectedCameraIndex]);
    }
  }, [cameraIds, selectedCameraId, selectedCameraIndex, setSelectedCameraIdWithLastCamera]);
  useEffect(() => {
    if (selectedCameraId === cameraIds[selectedCameraIndex]) {
      return;
    }
    setSelectedCameraIdWithLastCamera(cameraIds[selectedCameraIndex]);
    // skip update to selectedCameraId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraIds, selectedCameraIndex, setSelectedCameraIdWithLastCamera]);
  const goToNextCamera = useCallback(() => {
    setSelectedCameraIndex((prevIndex) => {
      // TODO this can be optimized
      for (let i = prevIndex + 1; i < cameraIds.length; i++) {
        if (cameraIds[i]) {
          return i;
        }
      }
      for (let i = 0; i < cameraIds.length; i++) {
        if (cameraIds[i]) {
          return i;
        }
      }
      return 0;
    });
  }, [cameraIds]);

  const goToCamera = useCallback(
    (index: number) => {
      if (!cameraIds[index]) {
        return;
      }
      setSelectedCameraIndex(index);
    },
    [cameraIds]
  );

  const goToPreviousCamera = useCallback(() => {
    setSelectedCameraIndex((prevIndex) => {
      // TODO this can be optimized
      for (let i = prevIndex - 1; i >= 0; i--) {
        if (cameraIds[i]) {
          return i;
        }
      }
      for (let i = cameraIds.length - 1; i >= 0; i--) {
        if (cameraIds[i]) {
          return i;
        }
      }
      return 0;
    });
  }, [cameraIds]);
  const goToLastCamera = useCallback(() => {
    setSelectedCameraIdWithLastCamera(lastCameraIdRef.current || cameraIds[0]);
  }, [cameraIds, setSelectedCameraIdWithLastCamera]);
  // handle joystick hotkeys
  useEffect(() => {
    if (!currentAction) {
      return;
    }
    if (currentAction.type === VideoHotkeyAction.NEXT_CAMERA) {
      goToNextCamera();
    } else if (currentAction.type === VideoHotkeyAction.PREVIOUS_CAMERA) {
      goToPreviousCamera();
    } else if (currentAction.type === VideoHotkeyAction.LAST_CAMERA) {
      goToLastCamera();
    }
  }, [currentAction, goToNextCamera, goToPreviousCamera, goToLastCamera]);
  return {
    selectedCameraId,
    goToNextCamera,
    goToPreviousCamera,
    goToLastCamera,
    setSelectedCameraId: setSelectedCameraIdWithLastCamera,
    goToCamera,
  };
};

export const PTZContext = createContext<PTZContextState>({
  panSpeed: CameraSpeedGear.NEUTRAL,
  zoomSpeed: CameraSpeedGear.NEUTRAL,
  tiltSpeed: CameraSpeedGear.NEUTRAL,
  setPanTiltSpeed: () => null,
  setZoomSpeed: () => null,
});

export const usePtzContext = (cameraId: string) => {
  const [panSpeed, setPanSpeed] = useState(CameraSpeedGear.NEUTRAL);
  const [zoomSpeed, setZoomSpeed] = useState(CameraSpeedGear.NEUTRAL);
  const [tiltSpeed, setTiltSpeed] = useState(CameraSpeedGear.NEUTRAL);

  const lastPanSpeed = useRef(CameraSpeedGear.NEUTRAL);
  const lastTiltSpeed = useRef(CameraSpeedGear.NEUTRAL);
  const lastZoomSpeed = useRef(CameraSpeedGear.NEUTRAL);

  const { cameraZoom, panTiltCamera } = useCameraPTZ();
  const setPanTiltSpeedWithMutation = useCallback(
    (pan: number, tilt: number) => {
      if (lastPanSpeed.current === pan && lastTiltSpeed.current === tilt) {
        // don't do anything if the last value is the same as the current value
        return;
      }
      lastPanSpeed.current = pan;
      lastTiltSpeed.current = tilt;
      setPanSpeed(pan);
      setTiltSpeed(tilt);
      panTiltCamera(cameraId, pan, tilt);
    },
    [cameraId, panTiltCamera]
  );

  const setZoomSpeedWithMutation = useCallback(
    (zoom: number) => {
      if (lastZoomSpeed.current === zoom) {
        // don't do anything if the last value is the same as the current value
        return;
      }
      lastZoomSpeed.current = zoom;
      setZoomSpeed(zoom);
      cameraZoom(cameraId, zoom);
    },
    [cameraId, cameraZoom]
  );

  return {
    panSpeed,
    zoomSpeed,
    tiltSpeed,
    setPanTiltSpeed: setPanTiltSpeedWithMutation,
    setZoomSpeed: setZoomSpeedWithMutation,
  };
};
